import type { CSSProperties } from "react";

// Raid roles and permission roles as stored in the API (db strings), plus Swedish UI labels.

export const RAID_ROLES = [
  "tank",
  "healer",
  "melee_dps",
  "ranged_dps",
] as const;
export type RaidRole = (typeof RAID_ROLES)[number];

export const RAID_ROLE_LABELS: Record<RaidRole, string> = {
  tank: "Tank",
  healer: "Healer",
  melee_dps: "Melee",
  ranged_dps: "Ranged",
};

// Ordered least → most privileged. New sign-ups are applicants.
export const PERMISSION_ROLES = [
  "applicant",
  "raider",
  "officer",
  "admin",
] as const;
export type PermissionRole = (typeof PERMISSION_ROLES)[number];

export const PERMISSION_ROLE_LABELS: Record<PermissionRole, string> = {
  applicant: "Sökande",
  raider: "Raider",
  officer: "Officer",
  admin: "Admin",
};

// Rank colours applied via inline `style` (the documented exception to semantic tokens).
// officer/raider are exact theme-independent hex; admin wires to the muted-foreground token so it
// tracks the theme (same computed colour as the `text-muted-foreground` class). applicant has no
// colour and renders in the default/muted text colour.
const PERMISSION_ROLE_COLORS: Partial<Record<PermissionRole, string>> = {
  raider: "#3b9dff",
  officer: "#ff8000",
  admin: "var(--muted-foreground)",
};

// Returns undefined for applicant/null/unknown — callers render the default (grey) text colour.
export function permissionRoleColorStyle(
  role: string | null | undefined,
): CSSProperties | undefined {
  if (!role) return undefined;
  const color = PERMISSION_ROLE_COLORS[role as PermissionRole];
  return color ? { color } : undefined;
}

// Authorization predicates. permissionRole is a plain string in the generated API types, so these accept string.
export const canManageRoster = (role: string): boolean =>
  role === "officer" || role === "admin";

// Raid-day management shares roster's privilege bar.
export const canManageRaids = canManageRoster;

export const isApplicant = (role: string): boolean => role === "applicant";
